import type { AxiosError, AxiosResponse } from 'axios'
import axios from 'axios'
import { ref } from 'vue'

import { FASTAPI_CONFIG } from '@/config/fastapi'
import { useAuthStore } from '@/stores/authStore'
import { isAbortError } from '@/utils/typeGuardUtil'

// FastAPI Backend Base URL
const FASTAPI_BASE_URL = FASTAPI_CONFIG.BASE_URL

const smsAuthClient = axios.create({
  baseURL: FASTAPI_BASE_URL,
  timeout: 10000, // 10秒超时
  withCredentials: true, // 允许发送和接收 cookies
  headers: {
    'Content-Type': 'application/json'
  }
})

export interface SmsSendCodeRequest {
  phone: string
  code_type?: string
}

export interface SmsSendCodeResponse {
  success: boolean
  message: string
  expires_in?: number
}

export interface SmsLoginRequest {
  phone: string
  code: string
}

export interface SmsLoginResponse {
  success: boolean
  message: string
  access_token: string
  token_type: string
  expires_in: number
  user_info: any
  is_new_user: boolean
}

export interface ApiResponse {
  success: boolean
  message: string
  data?: any
}

/**
 * Service for SMS authentication
 */
export const useSmsAuthService = () => {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // 手动重置加载状态的方法
  const resetLoadingState = () => {
    isLoading.value = false
    error.value = null
  }

  const handleApiError = (
    err: unknown,
    context: string,
    routeSpecificErrors?: Record<number, string>
  ): string => {
    if (!axios.isAxiosError(err))
      return err instanceof Error
        ? `${context}: ${err.message}`
        : `${context}: Unknown error occurred`

    const axiosError = err as AxiosError<ApiResponse>

    if (axiosError.response) {
      const { status, data } = axiosError.response

      if (routeSpecificErrors && routeSpecificErrors[status])
        return routeSpecificErrors[status]

      switch (status) {
        case 400:
          return `Bad request: ${data?.message || 'Invalid input'}`
        case 401:
          return 'Unauthorized: Authentication required'
        case 403:
          return `Forbidden: ${data?.message || 'Access denied'}`
        case 404:
          return `Not found: ${data?.message || 'Resource not found'}`
        case 409:
          return `Conflict: ${data?.message || 'Resource conflict'}`
        case 500:
          return `Server error: ${data?.message || 'Internal server error'}`
        default:
          return `${context}: ${data?.message || axiosError.message}`
      }
    }

    return `${context}: ${axiosError.message}`
  }

  const executeRequest = async <T>(
    requestCall: () => Promise<AxiosResponse<T>>,
    options: {
      errorContext: string
      routeSpecificErrors?: Record<number, string>
    }
  ): Promise<T | null> => {
    const { errorContext, routeSpecificErrors } = options

    isLoading.value = true
    error.value = null

    try {
      const response = await requestCall()
      return response.data
    } catch (err) {
      if (isAbortError(err)) {
        console.log(`${errorContext}: Request was aborted`)
        return null
      }

      const message = handleApiError(err, errorContext, routeSpecificErrors)
      error.value = message
      console.error(`${errorContext}: ${message}`, err)
      return null
    } finally {
      // 确保加载状态总是被重置
      isLoading.value = false
    }
  }

  const sendCode = async (
    phone: string,
    codeType: string = 'login'
  ): Promise<SmsSendCodeResponse> => {
    const result = await executeRequest<SmsSendCodeResponse>(
      () =>
        smsAuthClient.post('/api/v1/auth/sms/send-code', {
          phone,
          code_type: codeType
        }),
      {
        errorContext: 'Send SMS code',
        routeSpecificErrors: {
          400: '手机号格式不正确或发送过于频繁',
          429: '发送过于频繁，请稍后再试',
          500: '短信服务暂时不可用'
        }
      }
    )

    if (result) {
      return result
    } else {
      return {
        success: false,
        message: error.value || '发送验证码失败'
      }
    }
  }

  const login = async (credentials: SmsLoginRequest): Promise<boolean> => {
    try {
      const response = await executeRequest<SmsLoginResponse>(
        () => smsAuthClient.post('/api/v1/auth/sms/login', credentials),
        {
          errorContext: 'SMS login',
          routeSpecificErrors: {
            400: '验证码错误或已过期',
            401: '验证码验证失败',
            500: '登录服务暂时不可用'
          }
        }
      )

      if (response) {
        // 使用 authStore 处理登录成功
        const authStore = useAuthStore()

        // 保存 token
        authStore.setAuthData(
          response.user_info,
          response.access_token,
          'no-refresh-token' // 短信登录没有刷新token
        )

        return true
      }
      return false
    } catch (error: any) {
      console.error('SMS login error:', error)
      return false
    }
  }

  return {
    // State
    isLoading,
    error,

    // Methods
    sendCode,
    login,
    resetLoadingState
  }
}
